import type { StateCreator } from 'zustand'
import { apiService } from '@/service/api'

export interface InAppMeetingPanelPosition {
  x: number
  y: number
}

export interface StartInAppMeetingArgs {
  agentId?: string | null
  digitalCoworkerId?: string | null
  workspaceId?: string | null
  translatorMode?: boolean
  agentName?: string
  /** "Use Entire Data Catalog" for this call — persisted on the meeting token at start.
   *  Omitted ⇒ false ⇒ the board attached to the call is the context. */
  entireDataCatalog?: boolean
  /** Which agent the call's board tools ask (the user's Claude Direct setting). Omitted ⇒ native. */
}

export interface InAppMeetingState {
  /** Active in-app meeting token; null when no in-app call is running. */
  activeToken: string | null
  /** Whether the floating panel is collapsed into the minimized pill. */
  isMinimized: boolean
  /** Top-left position of the floating panel in viewport pixels. */
  panelPosition: InAppMeetingPanelPosition
  /** Display name of the agent driving the current call (for the pill avatar tooltip). */
  agentName?: string
  startInAppMeeting: (args: StartInAppMeetingArgs) => Promise<string>
  endInAppMeeting: () => Promise<void>
  toggleMinimize: () => void
  setPanelPosition: (pos: InAppMeetingPanelPosition) => void
}

/** Size of the expanded floating call panel. The Fabric call window docks beside it. */
export const IN_APP_PANEL_SIZE = { width: 380, height: 460 } as const

// Default panel anchors bottom-right with ~24px margin from the
// viewport edge. Resolved lazily so SSR / non-DOM test envs don't blow up.
const defaultPanelPosition = (): InAppMeetingPanelPosition => {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0 }
  }
  return {
    x: Math.max(window.innerWidth - IN_APP_PANEL_SIZE.width - 24, 24),
    y: Math.max(window.innerHeight - IN_APP_PANEL_SIZE.height - 24, 24),
  }
}

export const initialInAppMeetingState: Pick<
  InAppMeetingState,
  'activeToken' | 'isMinimized' | 'panelPosition' | 'agentName'
> = {
  activeToken: null,
  isMinimized: false,
  panelPosition: defaultPanelPosition(),
  agentName: undefined,
}

export const inAppMeetingSlice: StateCreator<
  InAppMeetingState,
  [['zustand/devtools', never]],
  [],
  InAppMeetingState
> = (set, get) => ({
  ...initialInAppMeetingState,

  startInAppMeeting: async (args: StartInAppMeetingArgs) => {
    const { agentName, ...rest } = args
    const response = await apiService.startMeetingInApp(rest)
    set({
      activeToken: response.meeting_token,
      isMinimized: false,
      agentName,
      panelPosition: get().panelPosition ?? defaultPanelPosition(),
    })
    return response.meeting_token
  },

  endInAppMeeting: async () => {
    const token = get().activeToken
    set({ activeToken: null, agentName: undefined, isMinimized: false })
    if (token) {
      // Best-effort — backend is idempotent for direct-mode tokens, so
      // a 404 / 500 here doesn't matter to the UI.
      try {
        await apiService.stopMeeting(token)
      } catch (error) {
        console.warn('endInAppMeeting: stopMeeting failed (ignored):', error)
      }
    }
  },

  toggleMinimize: () => {
    set({ isMinimized: !get().isMinimized })
  },

  setPanelPosition: (pos: InAppMeetingPanelPosition) => {
    set({ panelPosition: pos })
  },
})
